import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Tabs, Tab, Card, Chip, Button, Dialog, DialogTitle,
  DialogContent, IconButton, Grid, Stack, CircularProgress, Alert,
  CardActionArea
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import VideocamIcon from '@mui/icons-material/Videocam';
import Hls from 'hls.js'; // <-- Added HLS import

// Firebase imports
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase_config';

// Initialize callable functions
const resolveDisputeFn = httpsCallable(functions, 'resolveDispute');
const getRecordingPlaybackUrlFn = httpsCallable(functions, 'getRecordingPlaybackUrl'); // <-- Added callable

// --- NEW: HLS Video Player Component ---
const RecordingPlayer = ({ playbackUrl }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    if (!playbackUrl || !videoRef.current) return;

    const video = videoRef.current;
    let hls;

    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      // Safari Native HLS support
      video.src = playbackUrl;
    } else if (Hls.isSupported()) {
      // hls.js for Chrome, Firefox, Edge
      hls = new Hls();
      hls.loadSource(playbackUrl);
      hls.attachMedia(video);
    } else {
      console.warn("HLS is not supported in this browser.");
    }

    // Cleanup on unmount
    return () => {
      if (hls) hls.destroy();
    };
  }, [playbackUrl]);

  return (
    <video 
      ref={videoRef} 
      controls 
      style={{ width: '100%', borderRadius: '8px', backgroundColor: '#000' }} 
    />
  );
};


const DisputeManager = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal & Action states
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');

  // Video playback states
  const [playbackUrl, setPlaybackUrl] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoError, setVideoError] = useState('');

  // Fetch disputes from Firestore
  useEffect(() => {
    const q = query(collection(db, 'disputes'), orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const disputesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        studentName: doc.data().studentName || 'Student', 
        teacherName: doc.data().teacherName || 'Teacher',
        subject: doc.data().subject || 'Subject',
        date: doc.data().createdAt?.toDate().toLocaleDateString() || 'N/A',
        // IMPORTANT: Mapped session data needed for video fetch
        sessionId: doc.data().sessionId || null,
        recordingStatus: doc.data().recordingStatus || 'pending',
      }));
      setDisputes(disputesData);
      setLoading(false);
    }, (err) => {
      console.error("Error fetching disputes:", err);
      setError("Failed to load disputes.");
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const handleOpenModal = (dispute) => {
    setSelectedDispute(dispute);
    setIsModalOpen(true);
    setError('');
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedDispute(null);
    // Reset video states on close
    setPlaybackUrl(null);
    setVideoError('');
  };

  // --- NEW: Handle Video Fetch ---
  const handleLoadVideo = async () => {
    if (!selectedDispute?.sessionId) {
      setVideoError("Missing Session ID for this dispute.");
      return;
    }
    
    setVideoLoading(true);
    setVideoError('');
    
    try {
      const { data } = await getRecordingPlaybackUrlFn({ sessionId: selectedDispute.sessionId });
      setPlaybackUrl(data.playbackUrl);
    } catch (err) {
      console.error("Error loading video:", err);
      setVideoError(err.message || "Failed to secure playback URL.");
    } finally {
      setVideoLoading(false);
    }
  };

  const handleResolve = async (decision) => {
    if (!selectedDispute) return;
    
    setActionLoading(true);
    setError('');

    try {
      await resolveDisputeFn({
        disputeId: selectedDispute.id,
        decision: decision 
      });
      handleCloseModal();
    } catch (err) {
      console.error("Error resolving dispute:", err);
      setError(err.message || "An error occurred while resolving the dispute.");
    } finally {
      setActionLoading(false);
    }
  };

  const openDisputes = disputes.filter(d => d.status === 'open');
  const resolvedDisputes = disputes.filter(d => d.status !== 'open');
  
  const displayedDisputes = tabIndex === 0 ? disputes 
                          : tabIndex === 1 ? openDisputes 
                          : resolvedDisputes;

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" fontWeight="bold">Dispute Manager</Typography>
          <Typography variant="body2" color="text.secondary">
            Review and resolve payment disputes
          </Typography>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabIndex} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab label="All Disputes" />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Open
                {openDisputes.length > 0 && (
                  <Chip size="small" label={openDisputes.length} color="error" sx={{ height: 20, fontSize: '0.75rem' }} />
                )}
              </Box>
            } 
          />
          <Tab label="Resolved" />
        </Tabs>
      </Box>

      {/* Dispute List */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2}>
          {displayedDisputes.map((dispute) => (
            <Card key={dispute.id} variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={9}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {dispute.studentName} vs {dispute.teacherName}
                    </Typography>
                    <Chip 
                      label={dispute.status === 'open' ? 'Open' : 'Resolved'} 
                      size="small"
                      sx={{ 
                        bgcolor: dispute.status === 'open' ? 'warning.light' : 'grey.300',
                        color: dispute.status === 'open' ? 'warning.dark' : 'text.secondary',
                        fontWeight: 'bold'
                      }} 
                    />
                  </Box>
                  <Grid container spacing={4} sx={{ mb: 2 }}>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="caption" color="text.secondary">Amount</Typography>
                      <Typography variant="body2">{dispute.amount} ETB</Typography>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="caption" color="text.secondary">Subject</Typography>
                      <Typography variant="body2">{dispute.subject}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">Submitted</Typography>
                      <Typography variant="body2">{dispute.date}</Typography>
                    </Grid>
                  </Grid>
                  <Typography variant="body2" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {dispute.reason}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                  <Button variant="outlined" onClick={() => handleOpenModal(dispute)} sx={{ borderRadius: 2 }}>
                    Review
                  </Button>
                </Grid>
              </Grid>
            </Card>
          ))}
          {displayedDisputes.length === 0 && (
            <Typography color="text.secondary" textAlign="center" sx={{ py: 4 }}>
              No disputes found.
            </Typography>
          )}
        </Stack>
      )}

      {/* Dispute Details Modal */}
      <Dialog open={isModalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box>
            <Typography variant="h6" fontWeight="bold">Dispute Details</Typography>
            <Typography variant="body2" color="text.secondary">Review the dispute and take appropriate action</Typography>
          </Box>
          <IconButton onClick={handleCloseModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {selectedDispute && (
          <DialogContent dividers sx={{ pt: 3 }}>
            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            <Grid container spacing={3}>
              {/* LEFT COLUMN: Info & Actions */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>Session Information</Typography>
                <Card variant="outlined" sx={{ p: 2, mb: 4, borderRadius: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Student</Typography>
                      <Typography variant="body2">{selectedDispute.studentName}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Teacher</Typography>
                      <Typography variant="body2">{selectedDispute.teacherName}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Subject</Typography>
                      <Typography variant="body2">{selectedDispute.subject}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Amount</Typography>
                      <Typography variant="body1" fontWeight="bold">{selectedDispute.amount} ETB</Typography>
                    </Grid>
                  </Grid>
                </Card>

                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>Dispute Reason</Typography>
                <Box sx={{ bgcolor: '#fff5f5', p: 2, borderRadius: 2, border: '1px solid #ffebee', mb: 3 }}>
                  <Typography variant="body2">{selectedDispute.reason}</Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, bgcolor: '#fffcf0', p: 2, borderRadius: 2, border: '1px solid #ffecb3', mb: 3 }}>
                  <ErrorOutlineIcon color="warning" fontSize="small" sx={{ mt: 0.2 }} />
                  <Box>
                    <Typography variant="body2" fontWeight="bold">Current Status</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedDispute.status === 'open' 
                        ? `${selectedDispute.amount} ETB held in escrow` 
                        : 'Dispute has been resolved'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* RIGHT COLUMN: Video & Evidence */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>Video Evidence</Typography>
                
              {/* Video Implementation Container */}
              <Box sx={{ mb: 3 }}>
                {!selectedDispute.sessionId ? (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    No session ID attached to this dispute. Video unavailable.
                  </Alert>
                ) : playbackUrl ? (
                  <Box sx={{ overflow: 'hidden', borderRadius: 2, boxShadow: 2 }}>
                    <RecordingPlayer playbackUrl={playbackUrl} />
                  </Box>
                ) : (
                  <Card variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2, bgcolor: '#f9fafb', borderStyle: 'dashed' }}>
                    <VideocamIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Secure video recording may be available for this session.
                    </Typography>
                    <Button 
                      variant="contained" 
                      color="primary"
                      onClick={handleLoadVideo} 
                      disabled={videoLoading}
                      sx={{ borderRadius: 2, textTransform: 'none' }}
                    >
                      {videoLoading ? <CircularProgress size={24} color="inherit" /> : "Load Recording"}
                    </Button>
                    {videoError && <Alert severity="error" sx={{ mt: 2, textAlign: 'left' }}>{videoError}</Alert>}
                  </Card>
                )}
              </Box>

                {/* Legacy Audio Fallback (kept intact from original code) */}
                {selectedDispute.audioUrl && (
                  <Box sx={{ width: '100%', mt: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Additional Audio attached:
                    </Typography>
                    <audio controls src={selectedDispute.audioUrl} style={{ width: '100%', height: '40px' }}>
                      Your browser does not support the audio element.
                    </audio>
                  </Box>
                )}
              </Grid>
            </Grid>

            {/* Resolution Actions (Only show if open) */}
            {selectedDispute.status === 'open' && (
              <Box sx={{ mt: 2, pt: 3, borderTop: '1px solid #eee' }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>Resolution Actions</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <CardActionArea 
                      onClick={() => handleResolve('refund')} 
                      disabled={actionLoading}
                      sx={{ borderRadius: 2, height: '100%' }}
                    >
                      <Card variant="outlined" sx={{ p: 2, height: '100%', display: 'flex', alignItems: 'center', gap: 2, borderColor: '#ffcdd2', bgcolor: '#fafafa' }}>
                        <Box sx={{ bgcolor: '#ffebee', p: 1, borderRadius: '50%', display: 'flex' }}>
                          <HighlightOffIcon color="error" />
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="error.main" fontWeight="bold">Refund Student</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {selectedDispute.amount} ETB back to {selectedDispute.studentName}
                          </Typography>
                        </Box>
                      </Card>
                    </CardActionArea>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <CardActionArea 
                      onClick={() => handleResolve('pay')} 
                      disabled={actionLoading}
                      sx={{ borderRadius: 2, height: '100%' }}
                    >
                      <Card variant="outlined" sx={{ p: 2, height: '100%', display: 'flex', alignItems: 'center', gap: 2, borderColor: '#c8e6c9', bgcolor: '#fafafa' }}>
                        <Box sx={{ bgcolor: '#e8f5e9', p: 1, borderRadius: '50%', display: 'flex' }}>
                          <CheckCircleOutlineIcon color="success" />
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="success.main" fontWeight="bold">Pay Teacher</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Release funds to {selectedDispute.teacherName}
                          </Typography>
                        </Box>
                      </Card>
                    </CardActionArea>
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
        )}
      </Dialog>
    </Box>
  );
};

export default DisputeManager;